import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { newsApi, categoriesApi } from '../services/api';
import { News } from '../types';
import { getNewsImage, getNewsTitle, getNewsSubtitle, getNewsContent, getNewsSource, getNewsCategoryName, formatNewsDate, formatNewsDateShort } from '../services/newsHelpers';
import PageMeta from '../components/PageMeta';
import { getVideoEmbedUrl, getVideoPlatform } from '../services/videoUtils';
import '../styles/pages.css';
import '../styles/NewsSection.css';
import '../styles/ProjectsSection.css';

const NewsVideoEmbed: React.FC<{ news: News }> = ({ news }) => {
  if (!news.videoUrl) return null;
  const embedUrl = getVideoEmbedUrl(news.videoUrl);
  if (!embedUrl) return null;
  const isInstagram = getVideoPlatform(news.videoUrl) === 'instagram';
  return (
    <div className="proj-carousel-wrapper mb-4">
    <div
      className="proj-carousel"
      style={isInstagram ? { height: 'auto', minHeight: '435px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' } : {}}
    >
      {isInstagram ? (
        <iframe
          src={embedUrl}
          title={news.titulo}
          scrolling="no"
          allowTransparency
          style={{ border: 'none', borderRadius: '8px', width: 'min(600px, 100%)', height: '435px' }}
        />
      ) : (
        <iframe
          src={embedUrl}
          title={news.titulo}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
        />
      )}
    </div>
    </div>
  );
};

const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [news, setNews] = useState<News | null>(null);
  const [relatedNews, setRelatedNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [touchZoomed, setTouchZoomed] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;
      try {
        const [newsData, categoriesData, allNews] = await Promise.all([
          newsApi.getBySlug(slug),
          categoriesApi.getActive(),
          newsApi.getLatest(5),
        ]);

        if (newsData) {
          const category = categoriesData.find(cat => cat.id === newsData.categoriaId);
          setNews({ ...newsData, categoria: category || null });

          const related = allNews
            .filter(item => item.id !== newsData.id)
            .slice(0, 4)
            .map(newsItem => ({
              ...newsItem,
              categoria: categoriesData.find(c => c.id === newsItem.categoriaId) || null,
            }));
          setRelatedNews(related);
        } else {
          setNews(null);
        }
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
    setActiveSlide(0);
  }, [slug]);

  const fotos = news?.galeriaFotos?.fotos || [];

  const startTimer = useCallback((len: number) => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (len <= 1) return;
    autoplayRef.current = setInterval(() => setActiveSlide(i => (i + 1) % len), 4000);
  }, []);

  const stopTimer = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  }, []);

  useEffect(() => {
    startTimer(fotos.length);
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [fotos.length, startTimer]);

  const prevSlide = useCallback(() => {
    setActiveSlide(i => (i === 0 ? fotos.length - 1 : i - 1));
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  const nextSlide = useCallback(() => {
    setActiveSlide(i => (i === fotos.length - 1 ? 0 : i + 1));
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
    startTimer(fotos.length);
  }, [fotos.length, startTimer]);

  if (loading) {
    return (
      <div className="page-container">
        <Container className="py-5">
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        </Container>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="page-container">
        <Container className="py-5 text-center">
          <h2>Notícia não encontrada</h2>
          <Button variant="primary" className="mt-3" onClick={() => navigate('/noticias')}>
            Voltar para Notícias
          </Button>
        </Container>
      </div>
    );
  }

  const newsImage = getNewsImage(news);
  const newsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: getNewsTitle(news),
    description: getNewsSubtitle(news) || getNewsTitle(news),
    image: newsImage ? (newsImage.startsWith('http') ? newsImage : `https://www.agricampanha.agr.br${newsImage}`) : undefined,
    datePublished: news.dataNoticia,
    dateModified: news.dataNoticia,
    url: `https://www.agricampanha.agr.br/noticias/${news.slug}`,
    author: { '@type': 'Organization', name: 'Agricampanha', url: 'https://www.agricampanha.agr.br' },
    publisher: {
      '@type': 'Organization',
      name: 'Agricampanha',
      logo: { '@type': 'ImageObject', url: 'https://www.agricampanha.agr.br/img/logo.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.agricampanha.agr.br/noticias/${news.slug}` },
  };

  return (
    <div className="page-container">
      <PageMeta
        title={getNewsTitle(news)}
        description={getNewsSubtitle(news) || `${getNewsTitle(news)} - Notícias da Agricampanha`}
        image={newsImage || undefined}
        url={`/noticias/${news.slug}`}
        type="article"
        jsonLd={newsJsonLd}
      />
      <div className="page-header page-header--compact">
        <div className="page-header-bg" style={{ backgroundImage: "url('/img/hero-image.jpg')" }} />
        <div className="page-header-overlay" />
        <Container className="page-header-content page-header-content--left">
          <span className="section-badge section-badge--solid">{getNewsCategoryName(news)}</span>
          <h1>{getNewsTitle(news)}</h1>
          {getNewsSubtitle(news) && (
            <p className="page-header-subtitle">{getNewsSubtitle(news)}</p>
          )}
          <div className="page-header-meta">
            <i className="bi bi-calendar3 me-2"></i>
            {formatNewsDate(news.dataNoticia)}
          </div>
        </Container>
        <div className="page-header-fita" style={{ backgroundImage: "url('/img/bg-fita.png')" }} />
      </div>

      <Container className="news-detail-container">
        {/* Vídeo embed (se houver videoUrl) */}
        <NewsVideoEmbed news={news} />

        {/* Carrossel de fotos (se houver galeria e não houver vídeo) */}
        {!getVideoEmbedUrl(news.videoUrl || '') && fotos.length > 0 ? (
          <div
            className="proj-carousel-wrapper mb-4"
            onMouseEnter={stopTimer}
            onMouseLeave={() => startTimer(fotos.length)}
          >
            <div className="proj-carousel">
              {fotos.map((foto, index) => (
                <div
                  key={foto.id}
                  className={`proj-carousel-slide ${index === activeSlide ? 'proj-carousel-slide--active' : ''} ${touchZoomed && index === activeSlide ? 'proj-carousel-slide--zoomed' : ''}`}
                  style={{ backgroundImage: `url('${foto.url}')` }}
                  role="img"
                  aria-label={foto.legenda || `Foto ${index + 1}`}
                  onTouchStart={() => setTouchZoomed(true)}
                  onTouchEnd={() => setTouchZoomed(false)}
                />
              ))}
            </div>

            {fotos.length > 1 && (
              <>
                <button className="proj-carousel-arrow proj-carousel-arrow--prev" onClick={prevSlide} aria-label="Anterior">
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="proj-carousel-arrow proj-carousel-arrow--next" onClick={nextSlide} aria-label="Próxima">
                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="proj-carousel-dots">
                  {fotos.map((_, index) => (
                    <button
                      key={index}
                      className={`proj-carousel-dot ${index === activeSlide ? 'proj-carousel-dot--active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Ir para foto ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : !getVideoEmbedUrl(news.videoUrl || '') && getNewsImage(news) ? (
          /* Fallback: imagem única */
          <div className="proj-carousel-wrapper mb-4">
            <div className="proj-carousel">
              <div
                className="proj-carousel-slide proj-carousel-slide--active"
                style={{ backgroundImage: `url('${getNewsImage(news)}')` }}
                role="img"
                aria-label={getNewsTitle(news)}
              />
            </div>
          </div>
        ) : null}

        <div
          className="news-content"
          style={{ fontSize: '1.1rem', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}
        >
          {getNewsContent(news)}
        </div>

        <hr className="mt-5 mb-5" />

        <div className="text-center">
          <Button variant="primary" size="lg" onClick={() => navigate('/noticias')}>
            Ver outras notícias <i className="bi bi-arrow-right ms-1"></i>
          </Button>
        </div>

        {relatedNews.length > 0 && (
          <Row className="mt-5 pt-4">
            <Col xs={12}>
              <h2 className="text-center mb-4">Mais Notícias</h2>
            </Col>
            {relatedNews.map((item) => (
              <Col key={item.id} md={6} lg={3}>
                <Card as={Link} to={`/noticias/${item.slug}`} className="news-card">
                  <div
                    className="news-card-img-wrapper"
                    style={{ backgroundImage: `url(${getNewsImage(item)})` }}
                    role="img"
                    aria-label={getNewsTitle(item)}
                  >
                    <span className="news-card-category">{getNewsCategoryName(item)}</span>
                  </div>
                  <Card.Body className="news-card-body">
                    <div className="news-card-date">{formatNewsDateShort(item.dataNoticia)}</div>
                    <Card.Title className="news-card-title">{getNewsTitle(item)}</Card.Title>
                    <span className="news-card-read-more">
                      Ler mais <span className="arrow">→</span>
                    </span>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default NewsDetail;
